import React, { ReactNode } from "react";
import {
  Sequence,
  spring,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
} from "remotion";
import { Intro } from "../components/Intro";
import { Outro } from "../components/Outro";
import { ReelMetadata } from "../types";

export interface BaseTemplateProps {
  data: ReelMetadata;
  frame: number;
  fps: number;
  introFrames: number;
  bulletsFrames: number;
  outroFrames: number;
  headerOpacity: number;
  headerScale: number;
  headerTranslateY: number;
}

export interface ExtendedBaseTemplateProps extends BaseTemplateProps {
  theme?: string;
  background?: ReactNode;
  header?: ReactNode;
  insights?: ReactNode;
  phase2Style?: React.CSSProperties;
  IntroComponent?: React.ComponentType<any>;
  OutroComponent?: React.ComponentType<any>;
}

const IntroSequenceContent: React.FC<{
  IntroComponent: React.ComponentType<any>;
  data: ReelMetadata;
}> = ({ IntroComponent, data }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const scale = spring({
    frame,
    fps,
    from: 0.92,
    to: 1.0,
    config: { damping: 14, mass: 0.5, stiffness: 120 },
  });

  const translateY = spring({
    frame,
    fps,
    from: 30,
    to: 0,
    config: { damping: 14, mass: 0.6 },
  });

  const opacity = interpolate(frame, [0, 5], [0.3, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        flex: 1,
        width: "100%",
        transform: `scale(${scale}) translateY(${translateY}px)`,
        opacity,
        zIndex: 10,
        padding: "20px 0",
      }}
    >
      <IntroComponent
        headline={data.headline}
        keywords={data.keywords ?? []}
        introIcon={data.introIcon}
        frame={frame}
        fps={fps}
      />
    </div>
  );
};

const OutroSequenceContent: React.FC<{
  OutroComponent: React.ComponentType<any>;
  data: ReelMetadata;
}> = ({ OutroComponent, data }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const scale = spring({
    frame,
    fps,
    from: 0.92,
    to: 1.0,
    config: { damping: 14, mass: 0.5, stiffness: 120 },
  });

  const translateY = spring({
    frame,
    fps,
    from: 30,
    to: 0,
    config: { damping: 14, mass: 0.6 },
  });

  const opacity = interpolate(frame, [0, 5], [0.3, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        flex: 1,
        width: "100%",
        transform: `scale(${scale}) translateY(${translateY}px)`,
        opacity,
        zIndex: 10,
        padding: "20px 0",
      }}
    >
      <OutroComponent keywords={data.keywords ?? []} frame={frame} fps={fps} />
    </div>
  );
};

export const BaseTemplate: React.FC<ExtendedBaseTemplateProps> = ({
  data,
  introFrames,
  bulletsFrames,
  outroFrames,
  theme,
  background,
  header,
  insights,
  phase2Style = {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    gap: "24px",
  },
  IntroComponent = Intro,
  OutroComponent = Outro,
}) => {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        width: "100%",
      }}
    >
      {background}
      {/* Phase 1: Intro Hook */}
      <Sequence name="Intro Hook" durationInFrames={introFrames}>
        <IntroSequenceContent IntroComponent={IntroComponent} data={data} />
      </Sequence>
      {/* Phase 2: Bullets */}
      <Sequence
        name="Bullet Insights"
        from={introFrames}
        durationInFrames={bulletsFrames}
      >
        <div style={phase2Style}>
          {header}
          {insights}
        </div>
      </Sequence>
      {/* Phase 3: Outro CTA Screen */}
      <Sequence
        name="Outro CTA"
        from={introFrames + bulletsFrames}
        durationInFrames={outroFrames}
      >
        <OutroSequenceContent OutroComponent={OutroComponent} data={data} />
      </Sequence>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
